import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { getRateOrOrder } from '~/apis'
import Footer from '~/components/Footer'
import Header from '~/components/Header'
import { message } from '~/components/Message'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { useTokenAllowance } from '~/hooks/useTokenAllowance'
import {
  useDebounce,
  useUpdaterChainId,
  useGA,
  useIsHoT,
  useIsContractAddress,
} from '~/hooks'
import {
  useIsSendByRelayer,
  useMaxBtnGasLimit,
  useSettlementFeeValidator,
  useSlippage,
  useGasPrice,
  useGasLimitScale,
  useAmountLimits,
  useTokenPairInitByRouter,
} from './hooks'
import {
  useModalOpened,
  useModalToggle,
  useOneOfModalOpened,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { updatePendingChainId } from '~/state/user/actions'
import { SpotSwapUserTradeStatus } from '~/state/dex/type'
import {
  useSpotSwapPair,
  useSpotSwapAmount,
  useSpotSwapUserConfig,
  useTokenlonV6NetworkConfig,
  useIsDisableTrade,
} from '~/state/dex/hooks'
import {
  updateSpotSwapPair,
  updateSpotSwapAmount,
  updateSpotSwapPairAmount,
  updateSpotSwapUserConfig,
  updateSpotSwapUserTradeStatus,
} from '~/state/dex/actions'
import { useAppDispatch } from '~/state/hooks'
import { useNativeTokenBalance, useTokenBalance } from '~/state/wallet/hooks'
import { toBigNumber } from '~/utils/numbers'
import AllowanceArea from './components/AllowanceArea'
import PreviewButton from './components/PreviewButton'
import RateDetail from './components/RateDetail'
import GasFeeRow from './components/GasFeeRow'
import LabelFrom from './components/LabelFrom'
import SettingsModal from './components/SettingsModal'
import OrderPreview from './components/OrderPreview'
import OrderWarning from './components/OrderWarning'
import {
  formatAmount,
  formatInputAmount,
  validateInputAmount,
  validateInputAmountSendBySelf,
  showSkeletonStateList,
  formatETHCrossChain,
} from './helpers'
import {
  StyledExchangePage,
  StyledContent,
  StyledBoxContainer,
  StyledMainArea,
  StyledMainAreaInner,
  StyledIconSettings,
  StyledRedDot,
  StyledSettingsWrapper,
  StyledContainer,
  StyledFormContainer,
  StyledExchangeBox,
} from './styled'
import { RateState } from './types'
import { ExchangeTab } from '../ExchangeTab'
import Activity, { activityId } from './components/Activity'
import { constructTokenId, getIsNativeToken, getIsWeth } from '~/utils/token'
import { useWeb3React } from '@web3-react/core'
import { Box } from '../Box'
import { WrapETHForm } from '../WETH'
import AdBanner from '../AdBanner'
import TradeForm from '../TradeForm'
import { FeeIntroModal } from '~/components/FeeIntroModal'
import { useV6SDK } from '~/hooks/useV6SDK'
import PayWithTokensIntroModal from '~/components/PayWithTokensIntroModal'
import MiniSetPayModeModal from './components/MiniSetPayModeModal'
import SwapProtectionIntroModal from './components/SwapProtectionIntroModal'
import { TradeDirection } from '~/components/Exchange/types'
import { Order, permit2Address, allowanceTargetAddress } from '@tokenlon/v6-sdk'
import { formatUnits, formatEther, parseUnits, zeroAddress } from 'viem'
import Permit2IntroModal from '../Permit2IntroModal'
import SmartRoutingVisualizer from './components/SmartRoutingVisualizer'
import SmartRoutingDetailModal from './components/SmartRoutingVisualizer/SmartRoutingDetailModal'
import Tooltip from '../Tooltip'
import InteractiveMascot from './components/InteractiveMascot'
import GasFeeRiskCalculator from './components/GasFeeRiskCalculator'
import { useTokenPriceWithUseAPI } from '~/hooks/useTokenPrice'
import { isEqualIgnoreCase } from '~/utils/is'
import { isMainnetOrSepolia } from '~/utils/chain'
import TokensChangeArea from './components/TokenChangeArea'
import { OnSelectTokenFunc } from './components/TokenInputRow/types'
import { DEFAULT_GAS_LIMIT } from '~/constants/unit'
import { getLocalStorage } from '~/utils/common'
import { switchChain } from '~/utils/switchChain'
import Chart from './components/Chart'
import Recipient from './components/Recipient'
import { zkSync } from '~/model/chain/ethereum'

// @notice:
// 1.输入方，可输入小数位按 precision
// 2.前端展示数量：对手方/汇率/最小收到数量/交易费/矿工费最多6位小数
const SpotSwap: NextPage = () => {
  const { t } = useTranslation()
  const { trackElClickCurring, trackClick, trackView } = useGA()
  const router = useRouter()
  const { account, connector } = useWeb3React()
  const isWalletConnected = !!account
  const chainId = useUpdaterChainId()
  const dispatch = useAppDispatch()
  const sdk = useV6SDK()
  const isHoT = useIsHoT()

  const isMainnetOrSepoliaFlag = isMainnetOrSepolia(chainId)

  const [inputToken, outputToken] = useSpotSwapPair()
  const { inputAmount, outputAmount } = useSpotSwapAmount()
  const { sendByRelayer, isCrossSwap, recipient } = useSpotSwapUserConfig()
  const isSendByRelayer = useIsSendByRelayer()
  const isDisableTrade = useIsDisableTrade()

  const [gasFeeTokenAmount, setGasFeeTokenAmount] = useState(0n)
  const [minAmountOut, setMinAmountOut] = useState<bigint | undefined>()
  const [isNegativeOutputAmount, setIsNegativeOutputAmount] = useState(false)
  const [lockOrder, setLockOrder] = useState<Order | null>(null)
  // 这个字段仅用于记录代上链的提示是否打开，目的是为了记录打开情况，避免代上链的提示被重复打开
  const [
    openSendByRelayerTipShowFlag,
    setOpenSendByRelayerTipShowFlag,
  ] = useState<boolean>(sendByRelayer)
  // 这个字段用于记录代上链提示的显示状态，不同于上个字段，这个字段在打开后会有定时器3秒后自动关闭
  const [showOpenSendByRelayerTip, setShowOpenSendByRelayerTip] = useState<
    boolean
  >(false)
  const openSendByRelayerTipCloseTimer = useRef<NodeJS.Timer>()
  const showOpenSendByRelayerTipMountedRef = useRef(false)

  const [isUserEdited, setIsUserEdited] = useState(false)
  const [rate, setRate] = useState('0')
  // 为了实现绿灯动画播放完后更新表单，手动更新 smartRouting 组件的 order
  const [smartRoutingOrder, setSmartRoutingOrder] = useState<Order>()
  const [isNoQuote, setIsNoQuote] = useState(false)
  const inputTokenRef = useRef(inputToken?.address)
  const outputTokenRef = useRef(outputToken?.address)
  const inputAmountRef = useRef(inputAmount)
  const isFirstRender = useRef(true)

  const toggleSettingModal = useModalToggle(ApplicationModal.SPOT_SWAP_SETTINGS)
  const isOpenSettingModal = useModalOpened(ApplicationModal.SPOT_SWAP_SETTINGS)
  const toggleAllowanceModal = useModalToggle(
    ApplicationModal.ALLOWANCE_TOKEN,
    { stack: true },
  )
  const isAllowanceModalOpened = useModalOpened(
    ApplicationModal.ALLOWANCE_TOKEN,
  )
  const isReviewOpen = useModalOpened(ApplicationModal.SWAP_ORDER_PREVIEW)
  const togglePreviewModal = useModalToggle(ApplicationModal.SWAP_ORDER_PREVIEW)
  const toggleMiniSetPayModeModal = useModalToggle(
    ApplicationModal.MINI_SET_PAY_MODE,
  )
  const isMiniSetPayModeModalOpen = useModalOpened(
    ApplicationModal.MINI_SET_PAY_MODE,
  )

  const wethWrapOpened = useOneOfModalOpened([
    ApplicationModal.WETH_WRAP,
    ApplicationModal.WETH_PREVIEW,
    ApplicationModal.WETH_RESULT,
  ])

  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const [visitedSettings, setVisitedSettings] = useLocalStorage(
    'spotswap-settings-send-order-mode-visited',
    false,
  )

  const isVisited = getLocalStorage(activityId)
  const [isMarginWithActivity, setIsMarginWithActivity] = useState(!isVisited)
  useEffect(() => {
    setIsMarginWithActivity(!isVisited && !isReviewOpen)
  }, [isVisited, isReviewOpen])

  const isSettlementFeeValid = useSettlementFeeValidator(minAmountOut)

  const takerTokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: inputToken?.address,
  })
  const makerTokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: outputToken?.address,
    chainId: outputToken?.chainId,
  })
  const makerNativeTokenPrice = useTokenPriceWithUseAPI({
    tokenAddress: zeroAddress,
    chainId: outputToken?.chainId,
  })

  const hasTokenPrice = useMemo(() => {
    return Number(makerTokenPrice) > 0 && Number(takerTokenPrice) > 0
  }, [makerTokenPrice, takerTokenPrice])

  const isTakerETH = useMemo(() => inputToken?.address === zeroAddress, [
    inputToken?.address,
  ])

  const validInputAmount = useMemo(() => {
    const inputAmountBN = toBigNumber(inputAmount)

    return (
      !isWalletConnected ||
      !inputAmount ||
      (inputAmountBN.gt(0) && isSettlementFeeValid)
    )
  }, [inputAmount, isWalletConnected, isSettlementFeeValid])

  // 获取 token balance
  const ethBalance = useNativeTokenBalance()
  const tokenBalance = useTokenBalance(account, {
    address: inputToken?.address,
    decimals: inputToken?.decimals,
    name: '',
    symbol: inputToken?.symbol,
  })
  const ethBalanceStr = ethBalance?.toDecimalsFixed(ethBalance.decimals)
  const balance = isTakerETH
    ? ethBalanceStr
    : tokenBalance?.toDecimalsFixed(tokenBalance.decimals) || '0'

  const {
    defaultGasLimit: networkDefaultGasLimit,
  } = useTokenlonV6NetworkConfig()
  const defaultGasLimit = useMemo(() => {
    return BigInt(networkDefaultGasLimit || DEFAULT_GAS_LIMIT)
  }, [networkDefaultGasLimit])

  const { slippageBPS, slippageValue, setSlippageItem } = useSlippage()

  const maxBtnGasLimit = useMaxBtnGasLimit()
  const { quoteGasPrice, gasPriceWithScale } = useGasPrice()
  const gasLimitScale = useGasLimitScale()

  const debouncedInputAmount = useDebounce(inputAmount, 1500)

  const {
    isAmountOutOfRange,
    isAmountUnderMin,
    isAmountOverMax,
  } = useAmountLimits(debouncedInputAmount)

  const {
    data: rateOrOrder,
    error: getRateOrOrderError,
    isValidating,
  } = useSWR(
    debouncedInputAmount !== '' && !isReviewOpen && !isAmountOutOfRange
      ? [
          'getRateOrOrder',
          inputToken,
          outputToken,
          account,
          debouncedInputAmount,
          slippageBPS,
          isSendByRelayer,
          isReviewOpen,
          recipient,
        ]
      : null,
    () =>
      getRateOrOrder({
        sdk,
        inputToken,
        outputToken,
        account,
        amount: debouncedInputAmount,
        slippageBPS,
        isSendByRelayer: account ? isSendByRelayer : false,
        gasPrice: quoteGasPrice,
        defaultGasLimit,
        gasLimitScale,
        recipient,
      }),
    { refreshInterval: 4000 },
  )

  const order = useMemo(() => rateOrOrder?.order, [rateOrOrder])
  const usefulOrderInfo = useMemo(() => rateOrOrder?.usefulOrderInfo, [
    rateOrOrder,
  ])

  // @todo: 整理 rateState，让 SpotSwapUserTradeStatus.INQUIRING 能够根据 RateState.LOADING 变化
  const rateState = useMemo(() => {
    if (isNegativeOutputAmount || rate === '~') {
      return RateState.LOADING
    } else if (isValidating) {
      return isUserEdited ? RateState.LOADING : RateState.REFRESH_LOADING
    } else if (getRateOrOrderError) {
      return RateState.NetworkReason
    } else if (!!usefulOrderInfo?.rate) {
      return RateState.OK
    }
    return RateState.IDLE
  }, [
    usefulOrderInfo,
    getRateOrOrderError,
    isValidating,
    isNegativeOutputAmount,
    isUserEdited,
    rate,
  ])

  const isContractWallet = useIsContractAddress(account, chainId)

  const permit2Allowance = useTokenAllowance(
    inputToken,
    account,
    permit2Address[chainId],
  )

  const tokenlonAllowanceTarget = useTokenAllowance(
    inputToken,
    account,
    allowanceTargetAddress[chainId],
  )

  const isAllowanceTargetPriority = useMemo(() => {
    const isNotPermit2Allowance = !permit2Allowance || permit2Allowance?.lte(0)
    const hasTokenlonAllowanceTarget =
      tokenlonAllowanceTarget && tokenlonAllowanceTarget?.gt(0)
    const isZkSync = chainId === zkSync.chainId

    // @notice: zkSync 目前不支持 permit2 授权，升级为 v6.1.0 合约后可以尝试使用 permit2
    return (
      isZkSync ||
      (!isMainnetOrSepoliaFlag &&
        isNotPermit2Allowance &&
        hasTokenlonAllowanceTarget)
    )
  }, [
    permit2Allowance,
    tokenlonAllowanceTarget,
    isMainnetOrSepoliaFlag,
    chainId,
  ])

  const shouldApprove = useMemo(() => {
    const allowance1 = permit2Allowance ? permit2Allowance.toNumber() : 0
    const allowance2 = tokenlonAllowanceTarget
      ? tokenlonAllowanceTarget.toNumber()
      : 0
    const allowance = isAllowanceTargetPriority ? allowance2 : allowance1

    const allowanceNotEnough =
      parseUnits(inputAmount, inputToken?.decimals) > BigInt(allowance)
    return !isTakerETH && allowanceNotEnough
  }, [
    isTakerETH,
    inputToken?.decimals,
    inputAmount,
    permit2Allowance,
    tokenlonAllowanceTarget,
    isAllowanceTargetPriority,
  ])

  const isUseAllowanceTarget = useMemo(() => {
    return !shouldApprove && isAllowanceTargetPriority
  }, [shouldApprove, isAllowanceTargetPriority])

  // 点击 max 多预留一些避免 gasPrice 波动导致后续校验失败
  const estimateGasFee = formatEther(
    maxBtnGasLimit * ((gasPriceWithScale * 105n) / 100n),
  )

  // 普通闪兑只有 Mainnet 和 Sepolia 返回 gasUsed 有值，layer2 返回 0；跨链都会返回。
  const showGasFeeRow =
    !shouldApprove &&
    isWalletConnected &&
    hasTokenPrice &&
    (isCrossSwap || isMainnetOrSepoliaFlag)

  const showGasFeeRiskCalculator = useMemo(() => {
    return (
      !isSendByRelayer &&
      [RateState.OK, RateState.REFRESH_LOADING].includes(rateState) &&
      showGasFeeRow
    )
  }, [isSendByRelayer, rateState, showGasFeeRow])

  const onMax = () => {
    let takerAmount = ''
    const isMakerETH = outputToken?.address === zeroAddress
    if (isTakerETH) {
      const ethBalanceBN = toBigNumber(balance)
      if (ethBalanceBN.minus(estimateGasFee).lt(0)) {
        message.error(t('symbol_insufficient_balance_and_miner_fee'))
      } else {
        takerAmount = ethBalanceBN.minus(estimateGasFee).toString()
      }
    } else if (isMakerETH && isHoT) {
      const gasFeeValue = Number(estimateGasFee) * Number(makerNativeTokenPrice)
      const gasFeeTakerAmount = gasFeeValue / Number(takerTokenPrice)
      const calcTakerAmount = (Number(balance) - gasFeeTakerAmount).toString()
      if (Number(calcTakerAmount) <= 0) {
        message.error(t('hot_symbol_insufficient_balance_and_miner_fee'))
      } else {
        takerAmount = calcTakerAmount
      }
    } else {
      takerAmount = balance
    }
    dispatch(
      updateSpotSwapPairAmount({
        inputAmount: formatAmount(takerAmount, inputToken?.precision),
        outputAmount: '',
      }),
    )
    setIsUserEdited(true)
    setRate('~')
    dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.INQUIRING))
  }

  const handleInputFocus = (direction: TradeDirection) => {
    if (direction === TradeDirection.Output) return
    let newAmount = ''
    newAmount = formatInputAmount(inputAmount, inputToken?.precision)

    dispatch(
      updateSpotSwapAmount({
        direction,
        amount: newAmount,
      }),
    )
  }
  const handleInputChange = (direction: TradeDirection, amount: string) => {
    if (direction === TradeDirection.Output) return

    setMinAmountOut(undefined)
    const isCorrectInput = /^[0-9]*(\.[0-9]*)?$/.test(amount) || amount === ''
    if (isCorrectInput) {
      const takerAmount = formatInputAmount(amount, inputToken?.precision)
      dispatch(
        updateSpotSwapPairAmount({
          inputAmount: takerAmount,
          outputAmount: '',
        }),
      )
      setIsUserEdited(true)
      setRate('~')
      if (amount !== '') {
        dispatch(
          updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.INQUIRING),
        )
      }
    }
  }

  const onSelectToken: OnSelectTokenFunc = (inputToken, outputToken) => {
    dispatch(
      updateSpotSwapPair({
        inputToken,
        outputToken,
      }),
    )
    setIsUserEdited(true)
    setRate('~')
    clearQueryParams()
  }
  const onDirectionChange = async () => {
    let newInputToken = outputToken
    let newOutputToken = inputToken
    const newInputChainId = newInputToken.chainId
    const needSwitchNetwork = newInputChainId !== chainId

    if (needSwitchNetwork) {
      const newOutputTokenId = constructTokenId(
        newOutputToken.chainId,
        newOutputToken.address,
      )
      const newIsNativeToken = getIsNativeToken(newInputToken.address)
      const newIsWeth = getIsWeth(newInputToken.address, newInputToken.chainId)
      let crossChain = newInputToken.crossChain
      if (newIsNativeToken || newIsWeth) {
        crossChain = formatETHCrossChain(crossChain, isContractWallet)
      }
      const isValidCross = crossChain?.some((item) => {
        const { destinationChainId, destinationToken } = item
        const tokenId = constructTokenId(destinationChainId, destinationToken)
        return tokenId === newOutputTokenId
      })
      if (!isValidCross) {
        return
      }

      if (isWalletConnected) {
        await switchChain(connector, newInputChainId)
      }
      dispatch(updatePendingChainId(newInputChainId))
    }

    dispatch(
      updateSpotSwapPair({
        inputToken: outputToken,
        outputToken: inputToken,
      }),
    )
    setIsUserEdited(true)
    setRate('~')
    clearQueryParams()
  }

  const handleQuoteError = (reason: string) => {
    dispatch(
      updateSpotSwapPairAmount({
        inputAmount: '',
        outputAmount: '',
      }),
    )
    message.error(t(reason))
  }

  const handlePreview = async () => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
      return
    }

    if (shouldApprove) {
      toggleAllowanceModal()
      return
    }

    trackClick('webtl_instant_preview', {
      maker_token_symbol: outputToken.symbol,
      taker_token_symbol: inputToken.symbol,
      maker_token_amount: outputAmount,
      taker_token_amount: inputAmount,
      is_cross_swap: isCrossSwap,
    })

    // 可能自动重新获取订单，刚好没有订单，这时候不应该触发预览
    if (!rateOrOrder) return

    const lastOrder = { ...order }
    setLockOrder(lastOrder)

    if (isSendByRelayer) {
      const error = validateInputAmount({
        balance,
        inputToken,
        inputAmount,
        order: lastOrder,
        t,
      })
      if (error) {
        message.error(error)
        setLockOrder(null)
        return
      }
    } else {
      const error = validateInputAmountSendBySelfProxy(
        toggleMiniSetPayModeModal,
      )
      if (error) {
        setLockOrder(null)
        if (error === 'insufficient_balance_with_toggle_modal') return
        message.error(t(error))
        return
      }
    }
    togglePreviewModal()
    dispatch(
      updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.PREVIEWING_ORDER),
    )
  }

  const handleClickSettings = () => {
    setVisitedSettings(true)
    toggleSettingModal()
  }

  const validateInputAmountProxy = useCallback(() => {
    return validateInputAmount({
      balance,
      inputToken,
      inputAmount,
      order: lockOrder ?? order,
      t,
      returnRawError: true,
    })
  }, [balance, inputToken, inputAmount, lockOrder, order])

  const validateInputAmountSendBySelfProxy = useCallback(
    (handleInsufficientBalance?: () => void) => {
      const noop = () => {}
      return validateInputAmountSendBySelf({
        balance,
        inputToken,
        inputAmount,
        order: lockOrder ?? order,
        t,
        ethBalance: ethBalanceStr,
        gasFee: formatEther(gasFeeTokenAmount),
        isTakerETH,
        handleInsufficientBalance: handleInsufficientBalance || noop,
        returnRawError: true,
        isHoT,
        disableSetPayMode: isCrossSwap || !isMainnetOrSepoliaFlag,
      })
    },
    [
      balance,
      inputToken,
      inputAmount,
      lockOrder,
      order,
      ethBalanceStr,
      gasFeeTokenAmount,
      isTakerETH,
      toggleMiniSetPayModeModal,
      isHoT,
      isCrossSwap,
      isMainnetOrSepoliaFlag,
    ],
  )

  const handleToggleShowOpenSendByRelayerTip = (
    showOpenSendByRelayerTip: boolean,
  ) => {
    setShowOpenSendByRelayerTip(showOpenSendByRelayerTip)
    // 展示3秒后关闭
    openSendByRelayerTipCloseTimer.current &&
      clearTimeout(openSendByRelayerTipCloseTimer.current)
    if (showOpenSendByRelayerTip) {
      openSendByRelayerTipCloseTimer.current = setTimeout(() => {
        setShowOpenSendByRelayerTip(false)
      }, 3000)
    }
  }

  useEffect(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
    }
  }, [isDisableTrade])

  const clearQueryParams = () => {
    if (Object.keys(router.query).length > 0) {
      router.push('/instant', undefined, { shallow: true })
    }
  }

  useTokenPairInitByRouter()

  useEffect(() => {
    if (account) {
      dispatch(updateSpotSwapUserConfig({ recipient: account }))
    }
  }, [account])

  useEffect(() => {
    const referrer = document.referrer
    const currentHostname = window.location.hostname
    if (!referrer) {
      // Direct access (no referrer)
      trackView('webtl_instant_url')
    } else {
      try {
        const referrerHostname = new URL(referrer).hostname
        if (referrerHostname !== currentHostname) {
          // External access from other domain
          trackView('webtl_instant_url')
        }
      } catch (error) {
        console.error('Could not parse referrer URL:', referrer, error)
      }
    }

    dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.NORMAL))
  }, [])

  useEffect(() => {
    if (getRateOrOrderError) {
      // v6 SDK 返回的错误信息是带引号的字符串，需要去掉引号
      const error = getRateOrOrderError?.details?.replace(/^"(.*)"$/, '$1')
      if (error === 'risk_user') {
        handleQuoteError('risk_user')
      } else if (error === 'no quote') {
        setIsNoQuote(true)
      }
    } else {
      setIsNoQuote(false)
    }
  }, [getRateOrOrderError])

  useEffect(() => {
    // Resets isNoQuote when quote params are changed
    setIsNoQuote(false)
    if (inputToken?.chainId !== outputToken?.chainId) {
      dispatch(updateSpotSwapUserConfig({ isCrossSwap: true }))
    } else {
      dispatch(updateSpotSwapUserConfig({ isCrossSwap: false }))
    }
  }, [
    inputToken?.chainId,
    outputToken?.chainId,
    inputToken?.address,
    outputToken?.address,
    account,
    debouncedInputAmount,
    slippageBPS,
    isSendByRelayer,
    recipient,
  ])

  useEffect(() => {
    // 改变币对会改变交易数量为空
    if (debouncedInputAmount === '') {
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.NORMAL))
    }
  }, [debouncedInputAmount])

  useEffect(() => {
    if (!isFirstRender.current) {
      setIsUserEdited(true)
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.INQUIRING))
    }
  }, [isSendByRelayer])

  useEffect(() => {
    inputTokenRef.current = inputToken?.address
    outputTokenRef.current = outputToken?.address
    inputAmountRef.current = inputAmount
  }, [inputToken?.address, outputToken?.address, inputAmount])

  useEffect(() => {
    if (inputAmount !== '') {
      setRate('~')
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.INQUIRING))
    }
  }, [isSendByRelayer])

  // 根据最新的订单，计算数量
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    // 定时刷新的情况下，rateState 变为 ok 后触发绿灯动画播放，等待动画播放完再更新表单展示数量 src/components/Exchange/components/RateDetail/RateGreenDot.tsx
    const checkAndSetOrderInfo = () => {
      let isCorrectOrder = true
      const { fromAddress, toAddress, amountIn } = order
      isCorrectOrder =
        isEqualIgnoreCase(fromAddress, inputTokenRef.current) &&
        isEqualIgnoreCase(toAddress, outputTokenRef.current) &&
        Number(amountIn) === Number(inputAmountRef.current)
      // 因为 useSWR 设定了间隔10s自动发起新请求，用户编辑数量/切换方向/修改 token 会触发重新请求，所以这里需要判断是否是重新请求的订单还是之前自动请求的订单
      if (!isCorrectOrder) return

      const {
        rate,
        makerTokenAmount,
        minMakerTokenAmount,
        gasFeeTokenAmount,
      } = usefulOrderInfo

      const decimal = outputToken?.decimals
      const formattedMinMakerTokenAmount = formatUnits(
        minMakerTokenAmount,
        decimal,
      )

      const isNegativeAmount = toBigNumber(formattedMinMakerTokenAmount).lt(0)
      setMinAmountOut(minMakerTokenAmount)
      setIsNegativeOutputAmount(isNegativeAmount)
      if (isNegativeAmount) return

      setRate(rate)
      setSmartRoutingOrder(order)
      setGasFeeTokenAmount(gasFeeTokenAmount)
      dispatch(
        updateSpotSwapAmount({
          direction: TradeDirection.Output,
          amount: formatUnits(makerTokenAmount, decimal),
        }),
      )
      setIsUserEdited(false)
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.NORMAL))
    }

    if (usefulOrderInfo && order) {
      timer = setTimeout(checkAndSetOrderInfo, isUserEdited ? 0 : 2000)
    }
    return () => {
      timer && clearTimeout(timer)
    }
  }, [usefulOrderInfo])

  // 切换代上链开关时，展示提示。页面刷新时不展示，仅在变化时展示。
  useEffect(() => {
    if (showOpenSendByRelayerTipMountedRef.current) {
      if (isOpenSettingModal || isMiniSetPayModeModalOpen) {
        openSendByRelayerTipCloseTimer.current &&
          clearTimeout(openSendByRelayerTipCloseTimer.current)
        handleToggleShowOpenSendByRelayerTip(false)
      } else {
        // 仅在代上链状态改变时才更新状态
        if (openSendByRelayerTipShowFlag !== sendByRelayer) {
          setOpenSendByRelayerTipShowFlag(sendByRelayer)
          handleToggleShowOpenSendByRelayerTip(sendByRelayer)
        }
      }
    } else {
      showOpenSendByRelayerTipMountedRef.current = true
    }
  }, [isOpenSettingModal, isMiniSetPayModeModalOpen])

  return (
    <StyledExchangePage>
      <Head>
        <title>{t('meta_exchange_title')}</title>
        <meta name="description" content={t('meta_exchange_description')} />
        <meta name="keywords" content={t('meta_exchange_keywords')} />
      </Head>
      <Header />
      <StyledContent>
        <StyledContainer>
          <Chart />
          <StyledFormContainer withActivity={isMarginWithActivity}>
            {!isReviewOpen && <Activity />}
            {!wethWrapOpened && (
              <StyledExchangeBox hide={isReviewOpen}>
                <InteractiveMascot />
                <StyledBoxContainer root>
                  <ExchangeTab
                    controls={
                      isWalletConnected && (
                        <Tooltip
                          placement="right"
                          title={t('setting_send_order_by_relayer_toast')}
                          trigger="hover"
                          visible={showOpenSendByRelayerTip}
                        >
                          <StyledSettingsWrapper>
                            <StyledIconSettings
                              data-trackid="webtl_instant_setting"
                              onClick={(e) =>
                                trackElClickCurring(e)(handleClickSettings)
                              }
                            />
                            {!visitedSettings && <StyledRedDot />}
                          </StyledSettingsWrapper>
                        </Tooltip>
                      )
                    }
                  />
                  <StyledMainArea>
                    <StyledMainAreaInner>
                      <LabelFrom gasFee={estimateGasFee} onMax={onMax} />
                      <TokensChangeArea
                        rateState={rateState}
                        isAmountOutOfRange={isAmountOutOfRange}
                        handleInputFocus={handleInputFocus}
                        handleInputChange={handleInputChange}
                        onSelectToken={onSelectToken}
                        onDirectionChange={onDirectionChange}
                      />
                      {!isAmountOutOfRange &&
                        inputAmount &&
                        validInputAmount && (
                          <RateDetail rate={rate} rateState={rateState} />
                        )}
                    </StyledMainAreaInner>
                    {/* https://imtoken.atlassian.net/wiki/spaces/TL/pages/1368491560 当有报价时就展示 */}
                    {!isAmountOutOfRange &&
                      inputAmount &&
                      rate &&
                      validInputAmount && (
                        <>
                          <TradeForm.Divider />
                          <TradeForm.Stack gap={8}>
                            <SmartRoutingVisualizer
                              rateState={rateState}
                              routes={smartRoutingOrder?.routes}
                            />
                            {showGasFeeRow && (
                              <>
                                <GasFeeRow
                                  loading={showSkeletonStateList.includes(
                                    rateState,
                                  )}
                                  gasFeeTokenAmount={gasFeeTokenAmount}
                                />
                              </>
                            )}
                          </TradeForm.Stack>
                        </>
                      )}
                    {isWalletConnected && isCrossSwap && isContractWallet && (
                      <Recipient />
                    )}
                    {showGasFeeRiskCalculator && (
                      <GasFeeRiskCalculator
                        gasFeeTokenAmount={gasFeeTokenAmount}
                      />
                    )}
                    <OrderWarning
                      isNoQuote={isNoQuote}
                      minAmountOut={minAmountOut}
                      inputAmount={debouncedInputAmount}
                      hasTokenPrice={hasTokenPrice}
                    />
                    <PreviewButton
                      shouldApprove={shouldApprove}
                      rateState={rateState}
                      handlePreview={handlePreview}
                      validateInputAmountProxy={validateInputAmountProxy}
                      validateInputAmountSendBySelfProxy={
                        validateInputAmountSendBySelfProxy
                      }
                      minAmountOut={minAmountOut}
                      isAmountUnderMin={isAmountUnderMin}
                      isAmountOverMax={isAmountOverMax}
                    />
                  </StyledMainArea>
                </StyledBoxContainer>
              </StyledExchangeBox>
            )}

            {isOpenSettingModal && (
              <SettingsModal
                slippageValue={Number(slippageValue)}
                setSlippageItem={setSlippageItem}
              />
            )}
            {isAllowanceModalOpened && (
              <Box
                title={t('token_approval_box_title')}
                visible={isAllowanceModalOpened}
                onBack={toggleAllowanceModal}
              >
                <AllowanceArea token={inputToken} />
              </Box>
            )}
            {isReviewOpen && (
              <OrderPreview
                order={lockOrder}
                slippage={slippageBPS}
                isUseAllowanceTarget={isUseAllowanceTarget}
                setLockOrder={setLockOrder}
                setIsUserEdited={setIsUserEdited}
              />
            )}
            {wethWrapOpened && <WrapETHForm />}
          </StyledFormContainer>
        </StyledContainer>
      </StyledContent>
      <FeeIntroModal />
      <MiniSetPayModeModal />
      <PayWithTokensIntroModal />
      <Permit2IntroModal />
      <SwapProtectionIntroModal />
      {isMainnetOrSepoliaFlag && (
        <SmartRoutingDetailModal order={smartRoutingOrder} />
      )}
      <AdBanner />
      <Footer />
    </StyledExchangePage>
  )
}

export default SpotSwap
